<script>
import AdvisoryOpcoesTabela from '@modules/aawz-intelligence-v2/components/advisory-opcoes-tabela/advisory-opcoes-tabela.vue'
import {
  ROTULO_EXPANDIR_FUNDAMENTOS,
  ROTULO_RECOLHER_FUNDAMENTOS,
  TITULO_DA_CONSOLIDACAO,
} from '@modules/aawz-intelligence-v2/utils/painel-deliberacao-view'

export default {
  name: 'IntelligenceV2PainelOrquestrador',
  components: { AdvisoryOpcoesTabela },
  props: {
    // Já decorado por `decorarOrquestrador` — este componente não sabe se o
    // parecer veio do Orquestrador ou da derivação determinística, só o desenha.
    orquestrador: {
      type: Object,
      required: true,
    },
    // Prop aditiva (spec-011 [T063], `ui-components.md` §3): as alternativas
    // (`Option[]`) que o Orquestrador avaliou, mostradas ANTES da
    // recomendação (v2 §77A l.4089) — o gestor vê o leque de opções antes de
    // ler qual foi escolhida. Default `[]` preserva o comportamento atual:
    // sem opções, nenhum bloco novo é desenhado.
    opcoes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // Expansão dos fundamentos, por área. Local: nenhum outro pedaço da sala
      // precisa saber o que está aberto aqui.
      expandidos: {},
    }
  },
  computed: {
    titulo() {
      return TITULO_DA_CONSOLIDACAO
    },
    // Só quem TEM fundamento entra: rótulo para uma gaveta que não existe é
    // dado morto, e mascararia um botão renderizado por engano.
    rotulosDeFundamentos() {
      const rotulos = {}
      for (const area of this.orquestrador.porArea) {
        if (!area.temFundamentos) continue
        rotulos[area.chave] = this.expandidos[area.chave]
          ? ROTULO_RECOLHER_FUNDAMENTOS
          : ROTULO_EXPANDIR_FUNDAMENTOS
      }
      return rotulos
    },
    chavesExpansiveis() {
      return this.orquestrador.porArea
        .filter(function(area) { return area.temFundamentos })
        .map(function(area) { return area.chave })
    },
  },
  watch: {
    // Parecer novo: objeto novo, com todas as chaves reativas desde o início —
    // em Vue 2, chave acrescentada depois em objeto existente não é reativa. O
    // que já estava aberto continua aberto.
    chavesExpansiveis: {
      immediate: true,
      handler(chaves) {
        const estado = {}
        for (const chave of chaves) estado[chave] = Boolean(this.expandidos[chave])
        this.expandidos = estado
      },
    },
  },
  methods: {
    alternarArea(chave) {
      this.expandidos = { ...this.expandidos, [chave]: !this.expandidos[chave] }
    },
  },
}
</script>

<template>
  <div class="painel-orquestrador az-gap-small">
    <div class="az-ds-label-sentenca painel-orquestrador__titulo">
      {{ titulo }}
    </div>

    <div class="painel-orquestrador__bloco az-gap-xsmall">
      <div class="d-flex align-center flex-wrap az-gap-small">
        <icon-svg :name="orquestrador.icone" :size="16" color="primary" class="flex-shrink-0" />
        <span class="az-text-primary az-size-12 font-medium">{{ orquestrador.rotulo }}</span>
        <BaseStatus
          v-if="orquestrador.ehFallback"
          soft
          preserve-case
          :content="orquestrador.aviso"
          color="warning-new"
        />
      </div>

      <div v-if="orquestrador.explicacao" class="az-text-secondary az-size-12 az-muted">
        {{ orquestrador.explicacao }}
      </div>

      <!-- Prop aditiva [T063]: as alternativas avaliadas, ANTES da posição
           consolidada — o gestor vê o leque de opções antes da recomendação. -->
      <div v-if="opcoes.length" class="painel-orquestrador__opcoes az-gap-xsmall">
        <span class="az-text-primary az-size-12 font-medium">Opções avaliadas</span>
        <AdvisoryOpcoesTabela :opcoes="opcoes" />
      </div>

      <div
        v-if="orquestrador.posicaoConsolidada"
        class="az-text-secondary az-size-12 painel-orquestrador__prosa"
      >
        {{ orquestrador.posicaoConsolidada }}
      </div>

      <div
        v-for="area in orquestrador.porArea"
        :key="area.chave"
        class="painel-orquestrador__secao az-gap-xsmall"
      >
        <span class="az-text-primary az-size-12 font-medium">{{ area.label }}</span>
        <span class="az-text-secondary az-size-12 painel-orquestrador__posicao">
          {{ area.posicao }}
        </span>

        <button
          v-if="area.temFundamentos"
          type="button"
          class="az-ds-hyperlink painel-orquestrador__ver-fundamentos ia-texto-auxiliar"
          :aria-expanded="String(Boolean(expandidos[area.chave]))"
          :aria-label="rotulosDeFundamentos[area.chave] + ' de ' + area.label"
          @click="alternarArea(area.chave)"
        >
          <span>{{ rotulosDeFundamentos[area.chave] }}</span>
          <icon-svg
            name="arrow-down-new-ux"
            :size="12"
            color="var(--primary)"
            class="ia-block-seta"
            :class="expandidos[area.chave] ? 'ia-block-seta--aberta' : ''"
          />
        </button>

        <v-expand-transition>
          <div v-if="expandidos[area.chave]" class="painel-orquestrador__fundamentos az-gap-xsmall">
            <div
              v-for="fundamento in area.fundamentos"
              :key="fundamento.chave"
              class="painel-orquestrador__fundamento"
            >
              <span v-if="fundamento.afirmacao" class="az-text-secondary az-size-12">
                {{ fundamento.afirmacao }}
              </span>
              <span v-if="fundamento.fonte" class="az-text-secondary az-size-12 az-muted">
                — {{ fundamento.fonte }}
              </span>
            </div>
          </div>
        </v-expand-transition>
      </div>

      <div
        v-for="secao in orquestrador.secoes"
        :key="secao.chave"
        class="painel-orquestrador__secao az-gap-xsmall"
      >
        <div class="az-text-primary az-size-12 font-medium">{{ secao.titulo }}</div>
        <div v-for="item in secao.itens" :key="item.chave" class="az-text-secondary az-size-12">
          {{ item.texto }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.painel-orquestrador
  display: flex
  flex-direction: column

// O Orquestrador não é uma área — e a sala precisa dizer isso antes de qualquer
// palavra ser lida. Borda na cor de ação (não no cinza dos turnos) e fundo
// próprio: papel diferente, peso diferente. As cores de classe de contribuição
// (veda/alerta/propõe) não aparecem aqui de propósito — ele não contribui com
// domínio, arbitra o que os domínios disseram.
.painel-orquestrador__bloco
  display: flex
  flex-direction: column
  border-left: 2px solid var(--primary)
  border-radius: var(--radius-sm)
  background: var(--neutral-100)
  padding: 8px

.painel-orquestrador__secao
  display: flex
  flex-direction: column

// As opções vêm ANTES da posição consolidada — bloco próprio para não ler
// como se fizesse parte da prosa da recomendação logo abaixo.
.painel-orquestrador__opcoes
  display: flex
  flex-direction: column

.painel-orquestrador__prosa
  line-height: 1.45

// Mesmo piso de largura dos itens do turno: o rótulo da área é `flex-shrink-0`,
// e sem o piso a posição cedia até sobrar um caractere por linha no drawer.
// Bloco próprio abaixo do rótulo (não mais coluna ao lado dele): mesma entrelinha
// da prosa do orquestrador, para que as áreas leiam como um texto só.
.painel-orquestrador__posicao
  line-height: 1.45

// `contain` isola layout e paint: o `v-expand-transition` anima `height` fora da
// GPU e, sem isso, recalcula a página inteira a cada frame.
.painel-orquestrador__ver-fundamentos
  display: inline-flex
  align-items: center
  gap: 4px
  padding: 0
  border: 0
  background: none
  // A área é uma coluna flex: sem isto o link estica e a seta vai para a ponta.
  align-self: flex-start

  // `.ia-block-seta` traz `margin-left: auto` do cabeçalho do card; aqui a seta
  // é do rótulo.
  .ia-block-seta
    margin-left: 0

.painel-orquestrador__fundamentos
  contain: layout paint
  will-change: height
  display: flex
  flex-direction: column

.painel-orquestrador__fundamento
  line-height: 1.45
</style>
